<template>
  <div class="min-h-screen bg-beige-100 px-5 py-8 safe-area-top safe-area-bottom keyboard-container">
    <div class="max-w-sm mx-auto">
      <!-- Header with Logo -->
      <div class="text-center mb-10 animate-fade-in">
        <div class="flex items-center justify-center mb-4 mt-12">
          <img 
            src="/images/logo.png" 
            alt="Saskatoon Auto Connection Logo" 
            class="h-16 w-auto object-contain"
            @error="handleImageError"
          />
        </div>
      </div>

      <!-- AI Response Card -->
      <div class="card p-8 mb-8 animate-slide-up">
        <h2 class="text-2xl font-semibold text-warm-900 mb-4 text-display">
          We're here to help!
        </h2>
        
        <div class="text-warm-600 mb-6 text-body leading-relaxed">
          Thank you for your inquiry. We'll get back to you quickly.
        </div>


      </div>

      <!-- Detailed Inquiry Form -->
      <div class="card p-8 mb-8 animate-slide-up" style="animation-delay: 0.2s">
        <h3 class="font-semibold text-warm-900 mb-6 text-display">Get a detailed answer</h3>
        
        <div class="space-y-6">
          <!-- Pre-filled Original Question -->
          <div>
            <label class="block text-sm font-medium text-warm-700 mb-3 text-body">
              Your original question
            </label>
            <textarea
              ref="originalQuestionRef"
              v-model="originalQuestion"
              class="textarea-field min-h-[100px] keyboard-input text-body bg-warm-50"
              placeholder="Your question will appear here..."
              readonly
            />
            
          </div>

          <!-- Additional Details -->
          <div>
            <label class="block text-sm font-medium text-warm-700 mb-3 text-body">
              Additional details (optional)
            </label>
            <textarea
              ref="textareaRef"
              v-model="additionalDetails"
              class="textarea-field min-h-[120px] keyboard-input text-body"
              placeholder="Any additional information that would help us provide a better answer..."
              @focus="handleInputFocus"
              @blur="handleInputBlur"
            />
          </div>

          <!-- Contact Information -->
          <div v-if="shouldShowContactForm" class="border-t border-warm-200 pt-6">
            <h4 class="font-medium text-warm-800 mb-4 text-display">
              {{ hasCustomerInfo ? 'Contact Information (Optional)' : 'Contact Information' }}
            </h4>
            <div class="space-y-4">
              <div>
                <label class="block text-sm text-warm-600 mb-2 text-caption">Name</label>
                <input
                  type="text"
                  v-model="contactName"
                  class="input-field keyboard-input text-body"
                  :placeholder="customerName || 'Your name'"
                  @focus="handleInputFocus"
                  @blur="handleInputBlur"
                />
              </div>
              <div>
                <label class="block text-sm text-warm-600 mb-2 text-caption">
                  Phone Number {{ hasCustomerInfo ? '' : '(Required)' }}
                </label>
                <input
                  type="tel"
                  v-model="contactPhone"
                  class="input-field keyboard-input text-body"
                  :class="{ 'border-red-300': !hasCustomerInfo && !contactPhone.trim() }"
                  placeholder="(306) 123-4567"
                  @focus="handleInputFocus"
                  @blur="handleInputBlur"
                />
                <div v-if="!hasCustomerInfo && !contactPhone.trim()" class="mt-1 text-xs text-red-500">
                  Phone number is required
                </div>
              </div>
              <div>
                <label class="block text-sm text-warm-600 mb-2 text-caption">
                  Email {{ hasCustomerInfo ? '' : '(Optional)' }}
                </label>
                <input
                  type="email"
                  v-model="contactEmail"
                  class="input-field keyboard-input text-body"
                  placeholder="your.email@example.com"
                  @focus="handleInputFocus"
                  @blur="handleInputBlur"
                />
              </div>
              <div>
                <label class="block text-sm text-warm-600 mb-2 text-caption">Preferred contact method</label>
                <select v-model="preferredContact" class="input-field keyboard-input text-body" @focus="handleInputFocus" @blur="handleInputBlur">
                  <option value="phone">Phone call</option>
                  <option value="email">Email</option>
                  <option value="either">Either phone or email</option>
                </select>
              </div>
              <div>
                <label class="block text-sm text-warm-600 mb-2 text-caption">Best time to contact</label>
                <select v-model="preferredTime" class="input-field keyboard-input text-body" @focus="handleInputFocus" @blur="handleInputBlur">
                  <option value="">Any time</option>
                  <option value="morning">Morning (8AM - 12PM)</option>
                  <option value="afternoon">Afternoon (12PM - 5PM)</option>
                  <option value="evening">Evening (5PM - 8PM)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Customer Info Notice (when URL has customer info) -->
          <div v-if="!shouldShowContactForm" class="border-t border-warm-200 pt-6">
            <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 class="font-medium text-blue-800 mb-2 text-display">Thank you, {{ customerName }}.</h4>
              <p class="text-sm text-blue-700 text-body">
                We will do our best to address your concerns. We will contact you shortly.
              </p>
            </div>
          </div>

          <!-- Submit Button -->
          <button
            @click="submitInquiry"
            :disabled="!canSubmit || isLoading"
            class="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed text-body"
          >
            <span v-if="isLoading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
            <span v-else>Send My Inquiry</span>
          </button>
          
          <!-- Help Text -->
          <div v-if="!canSubmit && !isLoading" class="mt-2 text-center">
            <p class="text-sm text-warm-500">
              {{ hasCustomerInfo 
                ? 'Please enter your question or inquiry' 
                : 'Please enter your question and provide a phone number' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="card p-5 mb-6 bg-red-50 border-red-200 animate-scale-in">
        <p class="text-red-600 text-sm text-body">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useFeedbackStore } from '@/stores/feedback'
import { submitInquiry as submitInquiryAPI } from '@/services/api'

const router = useRouter()
const store = useFeedbackStore()

const originalQuestion = ref('')
const additionalDetails = ref('')
const contactName = ref('')
const contactPhone = ref('')
const contactEmail = ref('')
const preferredContact = ref('either')
const preferredTime = ref('')
const isLoading = ref(false)
const error = ref<string | null>(null)
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const originalQuestionRef = ref<HTMLTextAreaElement | null>(null)
const isKeyboardOpen = ref(false)

const customerName = computed(() => store.customerName || '')
const aiResponse = computed(() => store.feedback.aiResponse)

const shouldShowContactForm = computed(() => {
  // Show contact form when customer info is not available or is a default value
  return !customerName.value || customerName.value === 'Welcome!'
})

const hasCustomerInfo = computed(() => {
  // Customer has info if name exists and is not a default value
  return customerName.value && customerName.value !== 'Welcome!' && customerName.value !== 'John'
})

const canSubmit = computed(() => {
  const hasQuestion = originalQuestion.value.trim().length > 0
  
  // If customer info is available from URL, no contact info required
  if (hasCustomerInfo.value) {
    return hasQuestion
  }
  
  // If no customer info, phone number is required
  return hasQuestion && contactPhone.value.trim().length > 0
})

// Handle image loading error - fallback to text
function handleImageError(event: Event) {
  const img = event.target as HTMLImageElement
  const container = img.parentElement
  if (container) {
    container.innerHTML = `
      <h1 class="text-xl font-semibold text-warm-800 text-display leading-tight">
        Saskatoon Auto Connection
      </h1>
    `
  }
}

// Keyboard handling functions
function handleInputFocus(event: FocusEvent) {
  isKeyboardOpen.value = true
  // Small delay to ensure keyboard is fully open
  setTimeout(() => {
    const target = event.target as HTMLElement
    if (target) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      })
    }
  }, 300)
}

function handleInputBlur() {
  isKeyboardOpen.value = false
}

// Viewport change handler for additional keyboard detection
function handleViewportChange() {
  const currentHeight = window.innerHeight
  const initialHeight = window.screen.height
  const heightDifference = initialHeight - currentHeight
  
  // If height difference is significant, keyboard is likely open
  if (heightDifference > 150) {
    isKeyboardOpen.value = true
  } else {
    isKeyboardOpen.value = false
  }
}

onMounted(() => {
  // Pre-fill the original question from store
  originalQuestion.value = store.feedback.text || ''
  
  // Pre-fill customer name if available
  if (customerName.value !== 'Welcome!') {
    contactName.value = customerName.value
  }
  
  // Add viewport change listener
  window.addEventListener('resize', handleViewportChange)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleViewportChange)
})

async function submitInquiry() {
  if (!canSubmit.value) return
  
  isLoading.value = true
  error.value = null
  
  try {
    // Prepare inquiry data to match server expectations
    const inquiryData = {
      details: `${originalQuestion.value}\n\nAdditional details: ${additionalDetails.value}`,
      category: 'General Inquiry', // You can add category selection if needed
      contact: {
        name: contactName.value,
        phone: contactPhone.value,
        email: contactEmail.value,
        preferredContact: preferredContact.value,
        preferredTime: preferredTime.value
      }
    }
    
    // Submit using API service
    const result = await submitInquiryAPI(store.customer, store.feedback, inquiryData)
    
    if (result.success) {
      // Store inquiry data
      store.setComplaint({ 
        details: inquiryData.details,
        priority: 'normal',
        manager: 'brynn',
        caseNumber: result.caseNumber,
        contact: {
          phone: contactPhone.value,
          email: contactEmail.value,
          preferredTime: preferredTime.value
        }
      })
      
      router.push('/thank-you')
    } else {
      error.value = result.message || 'Failed to submit inquiry'
    }
  } catch (err) {
    error.value = 'Failed to submit inquiry. Please try again.'
    console.error('Inquiry submission error:', err)
  } finally {
    isLoading.value = false
  }
}


</script>

<style scoped>
.keyboard-container {
  transition: all 0.3s ease;
}

.keyboard-input {
  transition: all 0.2s ease;
  font-size: 16px !important; /* Prevent zoom on iOS */
}

/* iOS specific keyboard handling */
@supports (-webkit-touch-callout: none) {
  .keyboard-container {
    position: relative;
    overflow-x: hidden;
  }
  
  .keyboard-input:focus {
    transform: translateY(-8px);
  }
}

@media screen and (max-width: 768px) {
  .keyboard-input {
    font-size: 16px !important;
    transform-origin: center;
  }
}

/* Smooth content transitions */
.animate-fade-in {
  animation: fadeIn 0.6s ease-out;
}

.animate-slide-up {
  animation: slideUp 0.6s ease-out;
}

.animate-scale-in {
  animation: scaleIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style> 